/**
 * EntryModelModal Component
 *
 * Modal for creating/editing entry model items.
 * Form fields: name (required), description (optional), category dropdown (optional).
 * Handles archived category preservation when editing.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Link,
  TextArea,
  Select,
  SelectItem,
} from '@enfomentis/ui';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';

export interface EntryModelModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Existing entry model for edit mode (undefined for create) */
  entryModel: EntryModel | undefined;
  /** Active categories for the dropdown */
  categories: EntryModelCategory[];
  /** All categories (including archived) for looking up entry model's current category */
  allCategories: EntryModelCategory[];
  /** Handler for saving - receives form data */
  onSave: (data: { name: string; description?: string; entryModelCategoryId?: string }) => void;
  /** Whether save is in progress */
  isSaving?: boolean;
}

const NO_CATEGORY_KEY = '__none__';

export const EntryModelModal: React.FC<EntryModelModalProps> = ({
  isOpen,
  onClose,
  entryModel,
  categories,
  allCategories,
  onSave,
  isSaving = false,
}) => {
  const navigate = useNavigate();
  const isEditMode = !!entryModel;

  // Portal container state for Select popover (fixes aria-hidden warning in modals)
  // Using state instead of ref ensures re-render when container mounts
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const setPortalRef = useCallback((node: HTMLDivElement | null) => {
    setPortalContainer(node);
  }, []);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(NO_CATEGORY_KEY);

  // Build dropdown options: active categories + entry model's current category if archived
  const categoryOptions = useMemo(() => {
    const activeCategories = categories.filter(c => c.status !== 'archived');

    // If editing and entry model has a category not in active list, include it
    if (isEditMode && entryModel?.entryModelCategoryId) {
      const currentCategory = allCategories.find(c => c.id === entryModel.entryModelCategoryId);
      if (currentCategory && !activeCategories.some(c => c.id === currentCategory.id)) {
        return [...activeCategories, currentCategory];
      }
    }

    return activeCategories;
  }, [categories, allCategories, isEditMode, entryModel?.entryModelCategoryId]);

  // Reset form when modal opens/closes or entry model changes
  useEffect(() => {
    if (isOpen) {
      if (entryModel) {
        // Edit mode - populate form
        setName(entryModel.name);
        setDescription(entryModel.description || '');
        setSelectedCategoryId(entryModel.entryModelCategoryId || NO_CATEGORY_KEY);
      } else {
        // Create mode - reset form
        setName('');
        setDescription('');
        setSelectedCategoryId(NO_CATEGORY_KEY);
      }
    }
  }, [isOpen, entryModel]);

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const data: { name: string; description?: string; entryModelCategoryId?: string } = {
      name: trimmedName,
    };

    const trimmedDescription = description.trim();
    if (trimmedDescription) {
      data.description = trimmedDescription;
    }

    if (selectedCategoryId !== NO_CATEGORY_KEY) {
      data.entryModelCategoryId = selectedCategoryId;
    }

    onSave(data);
  };

  const isNameValid = name.trim().length >= 2;
  const canSave = isNameValid && !isSaving;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="lg"
      placement="top-center"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              <Tag className="w-5 h-5 text-primary" />
              <span>{isEditMode ? 'Edit Entry Model' : 'Add Entry Model'}</span>
            </ModalHeader>

            <ModalBody className="gap-4">
              <div ref={setPortalRef} className="flex flex-col gap-4">
                {/* Name */}
                <Input
                  label="Name"
                  placeholder="e.g., Break and Retest"
                  value={name}
                  onValueChange={setName}
                  isRequired
                  variant="bordered"
                  classNames={{ input: 'text-base' }}
                  isInvalid={name.length > 0 && !isNameValid}
                  errorMessage={
                    name.length > 0 && !isNameValid
                      ? 'Name must be at least 2 characters'
                      : undefined
                  }
                  autoFocus
                />

                {/* Description */}
                <TextArea
                  label="Description"
                  placeholder="Optional: Describe this entry model"
                  value={description}
                  onValueChange={setDescription}
                  variant="bordered"
                  classNames={{ input: 'text-base' }}
                  minRows={2}
                  maxRows={4}
                />

                {/* Category */}
                <Select
                  label="Category"
                  placeholder="Select a category (optional)"
                  classNames={{ value: 'text-base' }}
                  selectedKeys={[selectedCategoryId]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setSelectedCategoryId(selected || NO_CATEGORY_KEY);
                  }}
                  variant="bordered"
                  popoverProps={{
                    isNonModal: true,
                    shouldBlockScroll: false,
                    ...(portalContainer ? { portalContainer: portalContainer } : {}),
                  }}
                >
                  {[
                    <SelectItem key={NO_CATEGORY_KEY} textValue="No Category">
                      <span className="text-text-secondary">No Category</span>
                    </SelectItem>,
                    ...categoryOptions.map((cat) => (
                      <SelectItem key={cat.id} textValue={cat.name}>
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: cat.color }}
                          />
                          <span>{cat.name}</span>
                          {cat.status === 'archived' && (
                            <span className="text-xs text-text-tertiary">(archived)</span>
                          )}
                        </div>
                      </SelectItem>
                    )),
                  ]}
                </Select>

                {/* Manage Categories shortcut */}
                <Link
                  onPress={() => {
                    onClose();
                    navigate('/settings/strategies/reference-data?tab=categories');
                  }}
                  isDisabled={isSaving}
                  showAnchorIcon
                  size="sm"
                  className="cursor-pointer data-[disabled=true]:cursor-not-allowed"
                >
                  Manage Categories
                </Link>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isSaving}>
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={handleSave}
                isDisabled={!canSave}
                isLoading={isSaving}
              >
                {isEditMode ? 'Save Changes' : 'Add Entry Model'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
